/**
 * areaGraph — the pure seam layer of the connected walkable overworld (Round 3
 * Phase 2; notes/r3-design/overworld.md). Deterministic, no Phaser, no IO.
 *
 * The region is already a graph (REGION.nodes: grid x/y + connections[]). This
 * module derives, from that data alone, which EDGE of an area each neighbor sits
 * on, so walking off that edge transitions to the neighbor and spawns you at the
 * opposite edge. Town isn't a RegionNode — it's stitched in via TOWN_LINKS.
 *
 * Fast-travel (the RegionMap) remains alongside — walking is never forced.
 */
import type { RegionNode } from '@/shared';
import { REGION, regionNodeById } from './biomes.js';

export type Edge = 'up' | 'down' | 'left' | 'right';

export interface AreaLink {
  /** Target area id: a node id ('node-forest') or the literal 'Town'. */
  readonly toArea: string;
  /** Which edge of the CURRENT area this exit sits on. */
  readonly edge: Edge;
}

export const OPPOSITE: Record<Edge, Edge> = {
  up: 'down',
  down: 'up',
  left: 'right',
  right: 'left',
};

/** The authored Town seam: Town sits ABOVE the meadow (walk down from Town → meadow). */
export const TOWN_AREA = 'Town';
const TOWN_NEIGHBOR = 'node-meadow';
const TOWN_EDGE_IN_TOWN: Edge = 'down'; // Town's exit edge toward the meadow
// From the meadow's side, Town is up (the opposite edge).

/**
 * Compass direction from area `a` to neighbor `b`, from their region-grid coords.
 * Horizontal wins ties so the (mostly wider-than-tall) grid reads naturally.
 */
export function edgeBetween(a: RegionNode, b: RegionNode): Edge {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? 'right' : 'left';
  return dy >= 0 ? 'down' : 'up';
}

/**
 * All walkable exits from an area, derived from REGION connections (+ the Town
 * seam). When two neighbors would land on the same edge (grid diagonals), the
 * later one keeps its edge only if free, else it takes its vertical fallback —
 * every connection stays reachable and no two signposts overlap.
 */
export function exitsFor(areaId: string): readonly AreaLink[] {
  if (areaId === TOWN_AREA) {
    return [{ toArea: TOWN_NEIGHBOR, edge: TOWN_EDGE_IN_TOWN }];
  }
  const node = regionNodeById(areaId);
  if (!node) return [];

  const links: AreaLink[] = [];
  const used = new Set<Edge>();
  const claim = (toArea: string, primary: Edge, fallback?: Edge): void => {
    const edge = !used.has(primary) ? primary : fallback && !used.has(fallback) ? fallback : null;
    if (!edge) return; // fully contended (can't happen on the real 8-node grid)
    used.add(edge);
    links.push({ toArea, edge });
  };

  // The meadow additionally links up to Town (the reverse of TOWN_LINKS).
  if (areaId === TOWN_NEIGHBOR) claim(TOWN_AREA, OPPOSITE[TOWN_EDGE_IN_TOWN]);

  for (const id of node.connections) {
    const other = regionNodeById(id);
    if (!other) continue;
    const primary = edgeBetween(node, other);
    const fallback: Edge = other.y >= node.y ? 'down' : 'up';
    claim(id, primary, primary === fallback ? undefined : fallback);
  }
  return links;
}

/** Logical spawn point just inside a given ENTRY edge of a w×h area. */
export function spawnAtEdge(
  edge: Edge,
  w: number,
  h: number,
): { x: number; y: number; facing: Edge } {
  const inset = 90; // just inside the seam band, so arrival never re-triggers the exit
  switch (edge) {
    case 'up':
      return { x: w / 2, y: inset, facing: 'down' };
    case 'down':
      return { x: w / 2, y: h - inset, facing: 'up' };
    case 'left':
      return { x: inset, y: h / 2, facing: 'right' };
    case 'right':
      return { x: w - inset, y: h / 2, facing: 'left' };
  }
}

/** Display name for a seam signpost ("↑ Whispering Forest"). */
export function areaDisplayName(areaId: string): string {
  if (areaId === TOWN_AREA) return 'Town';
  return regionNodeById(areaId)?.name ?? areaId;
}

/** Every walkable area id (Town + all region nodes) — handy for tests/tools. */
export function allAreaIds(): readonly string[] {
  return [TOWN_AREA, ...REGION.nodes.map((n) => n.id)];
}
